import type { AdminSport, AdminSportsbook, AdminSubAffiliate } from '../admin/api'
import { useMemo, useState } from 'react'
import { createSubAffiliate, deleteSubAffiliate, updateSubAffiliate } from '../admin/api'
import RelationPicker from '../admin/RelationPicker'
import { assetUrl } from '../api'
import { highlightLine } from '../highlight'
import { buildApprovalMapSnippet, buildDistributionSnippet, buildSnippet, DEFAULT_BOOKS } from '../snippet'

/**
 * /affiliate sub-affiliate editor (migration 0059). One page handles both
 * create (sub == null) and edit. Internal-only record — the sub-affiliate
 * never logs in; this is where the team keeps:
 *
 *   NAME + EMBED FLAG  → the flag is the ?aff= / subAffiliate value
 *   PROMOTED SPORTS    → informational (no gating), checkbox catalog
 *   LINKS BY BOOK      → the sub-affiliate's own book-issued URLs; blank =
 *                        that book renders UNLINKED in their embeds (house
 *                        links never carry sub-affiliate traffic)
 *   EMBED CODES        → copy-paste snippets with the flag baked in
 *
 * Built from the shared portal pieces: SportEditor's header EDIT →
 * SAVE/CANCEL mode toggle and field/message classes, RelationPicker for
 * the sports multi-select, the sport page's ad-affiliate-* URL rows, and
 * the /embed gallery's ew-card + b-code highlighted snippet panel. No
 * page-scoped CSS.
 */

interface Props {
  sub: AdminSubAffiliate | null
  sports: AdminSport[]
  sportsbooks: AdminSportsbook[]
  // slug of the saved record so the parent can navigate/select it.
  onSaved: (slug: string) => void
  onDeleted: () => void
}

export default function SubAffiliatePage({ sub, sports, sportsbooks, onSaved, onDeleted }: Props) {
  // Create (sub == null) is a blank form, so it opens in edit; existing
  // records open read-only behind EDIT →, like every other editor.
  const [mode, setMode] = useState<'view' | 'edit'>(sub ? 'view' : 'edit')
  const [name, setName] = useState(sub?.name ?? '')
  const [slug, setSlug] = useState(sub?.slug ?? '')
  const [sportIds, setSportIds] = useState<Set<number>>(
    () => new Set((sub?.sub_affiliate_sports ?? []).map(s => s.sport_id)),
  )
  const [links, setLinks] = useState<Map<number, string>>(
    () => new Map((sub?.sub_affiliate_links ?? []).map(l => [l.sportsbook_id, l.url])),
  )
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const sortedSports = useMemo(
    () => [...sports].sort((a, b) => a.name.localeCompare(b.name)),
    [sports],
  )
  const sortedBooks = useMemo(
    () => [...sportsbooks].sort((a, b) => a.name.localeCompare(b.name)),
    [sportsbooks],
  )

  const sportOptions = useMemo(
    () => sortedSports.map(s => ({ id: s.id, label: s.name })),
    [sortedSports],
  )
  const sportSelections = useMemo(
    () => [...sportIds].map(id => ({ id })),
    [sportIds],
  )

  // Embed snippets need a sport slug — offer the promoted sports that have
  // one, plus 'general' (distribution + map support it; the odds widget
  // has no general mode so its snippet hides when 'general' is picked).
  const promotedSlugs = useMemo(() => {
    const slugs = sortedSports
      .filter(s => sportIds.has(s.id) && s.slug)
      .map(s => s.slug!.toLowerCase())
    return [...slugs, 'general']
  }, [sortedSports, sportIds])
  const [snippetSport, setSnippetSport] = useState<string | null>(null)
  const effectiveSnippetSport = snippetSport && promotedSlugs.includes(snippetSport)
    ? snippetSport
    : promotedSlugs[0] ?? 'general'

  const flagValid = /^[a-z0-9][a-z0-9-]{0,63}$/.test(slug)
  const canSave = name.trim().length > 0 && flagValid && !busy
  const readOnly = mode === 'view'

  function cancel() {
    setName(sub?.name ?? '')
    setSlug(sub?.slug ?? '')
    setSportIds(new Set((sub?.sub_affiliate_sports ?? []).map(s => s.sport_id)))
    setLinks(new Map((sub?.sub_affiliate_links ?? []).map(l => [l.sportsbook_id, l.url])))
    setMode('view')
    setMsg(null)
  }

  function setLink(bookId: number, url: string) {
    setLinks((prev) => {
      const next = new Map(prev)
      if (url.trim()) next.set(bookId, url.trim())
      else next.delete(bookId)
      return next
    })
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      const payload = {
        name: name.trim(),
        slug,
        sport_ids: [...sportIds],
        links: [...links.entries()].map(([sportsbook_id, url]) => ({ sportsbook_id, url })),
      }
      if (sub) {
        await updateSubAffiliate(sub.id, payload)
      }
      else {
        // Two-step create: the create RPC takes name+slug only; sports and
        // links land in the follow-up full-replace update.
        const created = await createSubAffiliate(payload.name, payload.slug) as { id: number }
        if (payload.sport_ids.length || payload.links.length) {
          await updateSubAffiliate(created.id, payload)
        }
      }
      setMsg({ kind: 'ok', text: 'saved' })
      setMode('view')
      onSaved(slug)
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!sub) return
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete sub-affiliate "${sub.name}"? Their embeds keep working but every book renders unlinked.`)) return
    setBusy(true)
    setMsg(null)
    try {
      await deleteSubAffiliate(sub.id)
      onDeleted()
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
      setBusy(false)
    }
  }

  async function copy(key: string, text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      setTimeout(() => setCopied(c => (c === key ? null : c)), 1800)
    }
    catch {
      setCopied(null)
    }
  }

  const snippets: { key: string, label: string, code: string }[] = []
  if (flagValid) {
    if (effectiveSnippetSport !== 'general') {
      snippets.push({
        key: 'odds',
        label: 'LIVE ODDS',
        code: buildSnippet(effectiveSnippetSport, { theme: 'default', size: 'auto', format: 'american', books: DEFAULT_BOOKS }, slug),
      })
    }
    snippets.push(
      { key: 'dist', label: 'DISTRIBUTION', code: buildDistributionSnippet(effectiveSnippetSport, undefined, slug) },
      { key: 'map', label: 'AVAILABILITY MAP', code: buildApprovalMapSnippet(effectiveSnippetSport, slug) },
    )
  }

  return (
    <section className={`ad-editor ad-editor--full${readOnly ? ' is-read-only' : ''}`}>
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          <h2 className="b-h2">{sub ? sub.name : 'NEW SUB-AFFILIATE'}</h2>
        </div>
        <div className="ad-editor-actions">
          {mode === 'view' && (
            <button
              type="button"
              onClick={() => { setMode('edit'); setMsg(null) }}
              className="b-btn b-btn-primary"
            >
              EDIT →
            </button>
          )}
          {mode === 'edit' && (
            <>
              {sub && (
                <button type="button" onClick={remove} disabled={busy} className="b-btn-ghost">
                  DELETE
                </button>
              )}
              {sub && (
                <button type="button" onClick={cancel} disabled={busy} className="b-btn-ghost">
                  CANCEL
                </button>
              )}
              <button type="button" onClick={save} disabled={!canSave} className="b-btn b-btn-primary">
                {busy ? 'SAVING…' : sub ? 'SAVE →' : 'CREATE →'}
              </button>
            </>
          )}
        </div>
      </header>
      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>
          {msg.kind === 'err' ? '⚠' : '✓'} {msg.text}
        </p>
      )}

      <div className="ad-editor-section">
        <h3 className="b-mono">IDENTITY</h3>
        <div className="ad-editor-row">
          <label className="ad-field">
            <span className="b-mono-dim b-tiny">NAME</span>
            <input
              className="ad-input"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={readOnly}
              placeholder="Acme Sports Media"
              spellCheck={false}
            />
          </label>
          <label className="ad-field">
            <span className="b-mono-dim b-tiny">EMBED FLAG</span>
            <input
              className="ad-input"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase())}
              disabled={readOnly}
              placeholder="acme"
              spellCheck={false}
            />
          </label>
        </div>
        <p className="b-mono-dim ad-section-hint">
          The flag is the <code>subAffiliate</code> value in their embed codes
          (lowercase letters, digits, dashes). Their embeds swap every book
          link to the URLs below — books with no URL render unlinked, never
          our house links.
        </p>
        {!flagValid && slug.length > 0 && (
          <p className="ad-msg b-mono is-err">⚠ flag: lowercase letters, digits, dashes only</p>
        )}
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">PROMOTED SPORTS</h3>
        <p className="b-mono-dim ad-section-hint">
          Which sports this sub-affiliate promotes. Informational — it doesn't
          gate their embeds; it drives the embed-code sport picker below.
        </p>
        <RelationPicker
          label="SPORTS"
          options={sportOptions}
          selected={sportSelections}
          onChange={next => setSportIds(new Set(next.map(s => s.id)))}
          readOnly={readOnly}
        />
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">
          LINKS BY BOOK ·
          {' '}
          {links.size}
        </h3>
        <p className="b-mono-dim ad-section-hint">
          The sub-affiliate's own book-issued URLs, one per book. Blank = that
          book shows unlinked in their embeds.
        </p>
        <div className="ad-affiliate-list">
          {sortedBooks.map((b) => {
            const hasBrand = b.brand_color != null
            const hasLogo = b.logo_url != null
            return (
              <label key={b.id} className="ad-affiliate-row">
                <span className="ad-affiliate-label b-mono">
                  {(hasBrand || hasLogo) && (
                    <span
                      className={`ad-affiliate-logo${b.logo_fill ? ' is-fill' : ''}`}
                      style={{ background: b.brand_color ?? 'transparent' }}
                      aria-hidden="true"
                    >
                      {hasLogo
                        ? <img src={assetUrl(b.logo_url!)} alt="" loading="lazy" />
                        : <span className="ad-affiliate-logo-text">{b.name.slice(0, 2).toUpperCase()}</span>}
                    </span>
                  )}
                  {b.name}
                </span>
                <input
                  type="url"
                  value={links.get(b.id) ?? ''}
                  onChange={e => setLink(b.id, e.target.value)}
                  disabled={readOnly}
                  placeholder="https://…"
                  spellCheck={false}
                  className="ad-input ad-affiliate-input"
                />
              </label>
            )
          })}
        </div>
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">EMBED CODES</h3>
        {!flagValid
          ? <p className="b-mono-dim ad-list-empty">set a valid embed flag to generate snippets</p>
          : (
              <>
                <div className="ad-editor-row">
                  <label className="ad-field">
                    <span className="b-mono-dim b-tiny">SPORT</span>
                    <select
                      className="ad-input"
                      value={effectiveSnippetSport}
                      onChange={e => setSnippetSport(e.target.value)}
                    >
                      {promotedSlugs.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                </div>
                {snippets.map(sn => (
                  <section key={sn.key} className="ew-card">
                    <div className="ew-card-head">
                      <span className="b-mono">
                        ▣
                        {' '}
                        {sn.label}
                      </span>
                    </div>
                    <div className="ew-embed-body">
                      <div className="ew-embed-meta">
                        <span className="b-mono-dim">
                          {sn.code.split('\n').length}
                          {' '}
                          LINES
                        </span>
                        <button
                          type="button"
                          className={`b-btn b-btn-primary b-btn-tiny${copied === sn.key ? ' is-success' : ''}`}
                          onClick={() => copy(sn.key, sn.code)}
                        >
                          {copied === sn.key ? '✓ COPIED' : 'COPY'}
                        </button>
                      </div>
                      <pre className="b-code">
                        {sn.code.split('\n').map((line, i) => (
                          <div key={i} className="b-code-line">
                            <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
                            <span className="b-code-text">{highlightLine(line, i)}</span>
                          </div>
                        ))}
                      </pre>
                    </div>
                  </section>
                ))}
              </>
            )}
      </div>
    </section>
  )
}
